import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, MapPin, Footprints, Clock } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { PageHero, SectionHead, Empty, CTAButton } from "@/components/Shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";

const TYPE = { safety_day: "Community Safety Day", walk: "Annual Walk", education: "Education Event", fundraiser: "Fundraiser" };
const DAY = ["Fence / project unveiling", "Swimming-lesson registration", "Water-safety demonstrations", "Life-jacket education", "CPR demonstrations", "Children's activities", "Rakim's Promise & Parent Pledge", "Sponsor & fence partner recognition", "Community resources"];
const WALK = ["Swim scholarships", "Pond projects", "Water-safety education", "CPR programs", "Community Safety Days"];

function RegisterDialog({ ev, onClose }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/events/${ev.id}/register`, { name, email });
      toast.success("You're registered!");
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["portal"] });
      onClose();
    } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl" data-testid="event-register-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase text-[#03045E]">{ev.title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input required placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} data-testid="event-reg-name" />
          <Input required type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="event-reg-email" />
          <button className="w-full rounded-full bg-[#03045E] text-white py-3 font-bold uppercase" data-testid="event-reg-submit">Register</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export const EventCard = ({ e, onRegister, registered }) => (
  <div className="rounded-2xl bg-white border border-sky-100 p-6 flex flex-col card-lift" data-testid={`event-card-${e.id}`}>
    {e.image && <img src={e.image} alt={`${e.title} flyer`} className="-mx-6 -mt-6 mb-5 w-[calc(100%+3rem)] max-w-none rounded-t-2xl object-cover" data-testid={`event-image-${e.id}`} />}
    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#0096C7]">{TYPE[e.type] || e.type}</span>
    <p className="font-display uppercase text-xl text-[#03045E] mt-1">{e.title}</p>
    <p className="text-sm text-slate-600 mt-2 flex items-center gap-2"><Calendar className="w-4 h-4" />{new Date(`${e.date}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</p>
    {e.time && <p className="text-sm text-slate-600 flex items-center gap-2"><Clock className="w-4 h-4" />{e.time}</p>}
    <p className="text-sm text-slate-600 flex items-center gap-2"><MapPin className="w-4 h-4" />{e.location}</p>
    {e.description && <p className="text-sm text-slate-600 mt-2 flex-1">{e.description}</p>}
    {e.presented_by && <p className="text-xs mt-3 uppercase tracking-wider text-slate-500">Presented by <b className="text-[#03045E]">{e.presented_by}</b></p>}
    {registered ? <p className="mt-4 text-sm font-bold text-green-600" data-testid={`event-registered-${e.id}`}>✓ You're registered</p>
      : <button onClick={() => onRegister(e)} className="mt-4 rounded-full bg-[#03045E] text-white py-2.5 text-sm font-bold uppercase" data-testid={`event-register-${e.id}`}>Register</button>}
  </div>
);

export default function Events() {
  const { data = [] } = useQuery({ queryKey: ["events"], queryFn: () => api.get("/events").then((r) => r.data) });
  const [ev, setEv] = useState(null);
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) { setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 150); }
  }, [hash]);
  return (
    <div data-testid="events-page">
      <PageHero eyebrow="Events • Community Safety Days" title="Every fence becomes an educational event." />
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        <SectionHead eyebrow="Upcoming" title="Find an event near you" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {data.length === 0 && <div className="sm:col-span-3"><Empty testid="events-empty">No events are scheduled yet — check back soon, or volunteer to help plan the next one.</Empty></div>}
          {data.map((e) => <EventCard key={e.id} e={e} onRegister={setEv} />)}
        </div>
      </section>
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10">
          <div><SectionHead eyebrow="Swim for Rakim™ Community Water Safety Day" title="What happens at a Safety Day" /><ul className="grid sm:grid-cols-2 gap-2 mt-6">{DAY.map((d) => <li key={d} className="rounded-xl sky-bg px-4 py-3 text-sm font-semibold text-[#03045E]">{d}</li>)}</ul></div>
          <div id="walk" className="rounded-3xl bg-[#03045E] text-white p-8 scroll-mt-28" data-testid="annual-walk">
            <Footprints className="w-10 h-10 text-[#FF6B35]" />
            <p className="text-xs uppercase tracking-[0.2em] text-sky-300 font-bold mt-4">Second Annual Swim for Rakim Walk</p>
            <p className="font-script text-4xl mt-2">Walk with purpose. Swim with safety. Live with hope.</p>
            <p className="font-display uppercase text-2xl mt-4">Every step helps fund the next child and the next pond.</p>
            <p className="text-sky-200 text-sm mt-4">Walk proceeds may be allocated, according to disclosed campaign terms, toward:</p>
            <div className="flex flex-wrap gap-2 mt-3">{WALK.map((w) => <span key={w} className="text-xs rounded-full border border-sky-400/30 px-3 py-1.5">{w}</span>)}</div>
            <CTAButton to="/volunteer" variant="orange" className="mt-6" testid="walk-volunteer-cta">Volunteer for the Walk</CTAButton>
          </div>
        </div>
      </section>
      {ev && <RegisterDialog ev={ev} onClose={() => setEv(null)} />}
    </div>
  );
}
